/**
 * <collections-header>
 * Tabbed style picker for PLP. Each tile links to a separate collection.
 * Clicking a tile swaps the product-grid section in place (no full reload)
 * using Shopify's section rendering API, then pushState's the new URL.
 * Falls back to normal navigation if anything is missing or fetch fails.
 */
class CollectionsHeader extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector("[data-collections-header-track]");
    this.nextArrow = this.querySelector("[data-collections-header-next]");
    this.loadingRegion = this.querySelector(".collections-header__loading");

    this.onTileClick = this.onTileClick.bind(this);
    this.onPopState = this.onPopState.bind(this);
    this.updateArrow = this.updateArrow.bind(this);
  }

  connectedCallback() {
    this.addEventListener("click", this.onTileClick);
    window.addEventListener("popstate", this.onPopState);

    if (this.nextArrow) {
      this.nextArrow.addEventListener("click", () => {
        this.track.scrollBy({ left: this.track.clientWidth * 0.8, behavior: "smooth" });
      });
    }
    if (this.track) {
      this.track.addEventListener("scroll", this.updateArrow, { passive: true });
      window.addEventListener("resize", this.updateArrow);
      // Defer so layout is settled.
      requestAnimationFrame(this.updateArrow);
    }
  }

  disconnectedCallback() {
    window.removeEventListener("popstate", this.onPopState);
    window.removeEventListener("resize", this.updateArrow);
  }

  updateArrow() {
    if (!this.track || !this.nextArrow) return;
    const overflow = this.track.scrollWidth - this.track.clientWidth;
    const atEnd = this.track.scrollLeft >= overflow - 2;
    this.nextArrow.hidden = overflow <= 2 || atEnd;
  }

  /**
   * Resolve the product-grid section's Shopify wrapper id from the DOM.
   * `data-id` on the grid equals the section id used by ?section_id=.
   */
  getGridSectionId() {
    const grid = document.querySelector("#ProductGridContainer [data-id]");
    return grid ? grid.getAttribute("data-id") : null;
  }

  onTileClick(event) {
    const tile = event.target.closest(".collections-header__tile");
    if (!tile) return;

    // Respect modifier keys / middle-click → let the browser handle it.
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const url = tile.getAttribute("data-collection-url");
    const handle = tile.getAttribute("data-collection-handle");
    const sectionId = this.getGridSectionId();

    // No grid on the page or already active → fall back / no-op.
    if (!url || !sectionId) return;
    if (tile.classList.contains("collections-header__tile--active")) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    this.navigateTo(url, handle, sectionId, true);
  }

  onPopState() {
    const sectionId = this.getGridSectionId();
    if (!sectionId) return;

    const path = window.location.pathname;
    const tile = this.querySelector(`[data-collection-url^="${path}"]`);
    const handle = tile ? tile.getAttribute("data-collection-handle") : null;
    // Only intercept navigations that target one of our tab collections.
    if (!handle) return;

    this.navigateTo(window.location.href, handle, sectionId, false);
  }

  setLoading(isLoading) {
    this.classList.toggle("is-loading", isLoading);
    if (this.loadingRegion) {
      this.loadingRegion.textContent = isLoading ? "Loading products…" : "";
    }
  }

  setActiveTile(handle) {
    this.querySelectorAll(".collections-header__tile").forEach((tile) => {
      const isActive = tile.getAttribute("data-collection-handle") === handle;
      tile.classList.toggle("collections-header__tile--active", isActive);
      tile.setAttribute("aria-selected", isActive ? "true" : "false");
      if (isActive) {
        tile.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
      }
    });
    this.setAttribute("data-current-collection", handle);
  }

  navigateTo(url, handle, sectionId, pushState) {
    const wrapper = document.getElementById(`shopify-section-${sectionId}`);
    if (!wrapper) {
      window.location.assign(url);
      return;
    }

    this.setLoading(true);
    // Optimistically mark the tab active for immediate feedback.
    this.setActiveTile(handle);

    const fetchUrl = `${url.split("?")[0]}?section_id=${encodeURIComponent(sectionId)}`;

    fetch(fetchUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        wrapper.innerHTML = html;

        if (pushState) {
          window.history.pushState({ collectionsHeader: true }, "", url);
        }

        // Move keyboard/scroll focus to the refreshed grid for a11y, without jumping the page.
        const grid = document.getElementById("ProductGridContainer");
        if (grid) {
          grid.setAttribute("tabindex", "-1");
        }

        document.dispatchEvent(
          new CustomEvent("collections-header:loaded", {
            bubbles: true,
            detail: { handle, url, sectionId },
          })
        );
      })
      .catch(() => {
        // Hard fallback: navigate normally.
        window.location.assign(url);
      })
      .finally(() => {
        this.setLoading(false);
      });
  }
}

if (!customElements.get("collections-header")) {
  customElements.define("collections-header", CollectionsHeader);
}
